import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { ActiveStatusBadge } from '../../components/ui/ActiveStatusBadge';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { EditIconLink } from '../../components/ui/EditIconLink';
import { ListPaginationBar } from '../../components/ui/ListPaginationBar';
import { SortOrderControls } from '../../components/ui/SortOrderControls';
import { ViewIconLink } from '../../components/ui/ViewIconLink';
import { useSortOrderReorder } from '../../hooks/useSortOrderReorder';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import { canWriteProductCatalog } from '../../components/salesProducts/productCatalogAccess';
import { ProductAreaScope } from './productAreaScope';
import {
  SalesProduct,
  SalesProductSummary,
  SalesProductUpsertPayload,
  deleteSalesProduct,
  downloadSalesProductImportTemplate,
  getSalesProduct,
  getSalesProductImportStatus,
  importSalesProducts,
  listSalesProducts,
  updateSalesProduct,
} from '../../services/salesProductsApi';

function triggerBlobDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

function buildProductPayload(product: SalesProduct, sortOrder: number): SalesProductUpsertPayload {
  return {
    name: product.name,
    logoBase64: product.logoBase64,
    coverImageBase64: product.coverImageBase64,
    elevatorPitchHtml: product.elevatorPitchHtml,
    valuePropositionHtml: product.valuePropositionHtml,
    descriptionHtml: product.descriptionHtml,
    executiveSummaryHtml: product.executiveSummaryHtml,
    modulesAndFeaturesHtml: product.modulesAndFeaturesHtml,
    featuresHtml: product.featuresHtml,
    icpHtml: product.icpHtml,
    segmentsHtml: product.segmentsHtml,
    isActive: product.isActive,
    sortOrder,
  };
}

export function ProductsPage({ scope = 'commercial' }: { scope?: ProductAreaScope }) {
  const isProductArea = scope === 'product';
  const isOffer = scope === 'offer';
  const { canWriteModule } = usePermissions();
  const { confirm } = useConfirmDialog();
  const canWrite = isOffer
    ? false
    : isProductArea
      ? canWriteProductCatalog(canWriteModule)
      : canWriteModule('sales.products');
  const editPath = (id: string) => (isProductArea ? `/products/${id}` : `/sales/products/${id}/edit`);
  const newPath = isProductArea ? '/products/new' : '/sales/products/new';
  const [products, setProducts] = useState<SalesProductSummary[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(500);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const importInputRef = useRef<HTMLInputElement>(null);
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  async function loadData(targetPage = page): Promise<void> {
    setLoading(true);
    try {
      const result = await listSalesProducts({ activeOnly: false, page: targetPage, pageSize });
      setProducts(result.items);
      setTotalCount(result.totalCount);
      setPage(result.page);
    } catch {
      toast.error('Falha ao carregar produtos.');
    } finally {
      setLoading(false);
    }
  }

  const { sortedItems, moving, move } = useSortOrderReorder({
    items: products,
    canReorder: canWrite,
    updateItem: async (item, sortOrder) => {
      const full = await getSalesProduct(item.id);
      await updateSalesProduct(item.id, buildProductPayload(full, sortOrder));
    },
    onReordered: () => loadData(page),
  });

  useEffect(() => {
    void loadData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  async function handleDelete(id: string): Promise<void> {
    if (!canWrite) return;
    if (
      !(await confirm({
        title: 'Excluir produto',
        message: 'Excluir este produto?',
        confirmLabel: 'Excluir',
        tone: 'danger',
      }))
    ) {
      return;
    }
    try {
      await deleteSalesProduct(id);
      toast.success('Produto excluído.');
      await loadData(page);
    } catch {
      toast.error('Falha ao excluir produto.');
    }
  }

  async function handleDownloadTemplate(): Promise<void> {
    try {
      const blob = await downloadSalesProductImportTemplate();
      triggerBlobDownload(blob, 'produtos-template.csv');
      toast.success('Template baixado.');
    } catch {
      toast.error('Falha ao baixar template.');
    }
  }

  async function pollImportStatus(importId: string): Promise<void> {
    for (let attempt = 0; attempt < 30; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      try {
        const status = await getSalesProductImportStatus(importId);
        if (status.status === 'Completed') {
          toast.success(
            `Importação concluída: ${status.createdCount} criados, ${status.updatedCount} atualizados.`,
          );
          await loadData(page);
          return;
        }
        if (status.status === 'Failed') {
          toast.error(status.errorSummary || 'Importação falhou.');
          return;
        }
      } catch {
        // Continua tentando enquanto o job processa.
      }
    }
    toast.info('Importação ainda em andamento. Atualize a lista quando concluir.');
  }

  async function handleImportFile(file: File | undefined): Promise<void> {
    if (!file || !canWrite) return;
    setImporting(true);
    try {
      const queued = await importSalesProducts(file);
      toast.success(
        queued.hangfireJobId
          ? `Importação enfileirada (Hangfire ${queued.hangfireJobId}).`
          : 'Importação enfileirada.',
      );
      void pollImportStatus(queued.importId);
    } catch {
      toast.error('Falha ao enviar arquivo de importação.');
    } finally {
      setImporting(false);
      if (importInputRef.current) {
        importInputRef.current.value = '';
      }
    }
  }

  return (
    <PageShell>
      <PageHeader
        title="Produtos"
        description={
          isOffer
            ? 'Consulta da oferta para uso durante a venda.'
            : isProductArea
              ? 'Cadastro central dos produtos, com backlog, roadmap, riscos, problemas e mudanças.'
              : 'Cadastro dos produtos usado na oferta. Preços são administrados por quem tem permissão comercial.'
        }
        actions={
          isOffer ? undefined : (
            <div className="erp-btn-actions">
              <button
                type="button"
                className="erp-btn-secondary"
                onClick={() => void handleDownloadTemplate()}
              >
                Template CSV
              </button>
              {canWrite ? (
                <>
                  <input
                    ref={importInputRef}
                    type="file"
                    accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    className="hidden"
                    onChange={(event) => void handleImportFile(event.target.files?.[0])}
                  />
                  <button
                    type="button"
                    className="erp-btn-secondary"
                    disabled={importing}
                    onClick={() => importInputRef.current?.click()}
                  >
                    {importing ? 'Importando...' : 'Importar'}
                  </button>
                  <Link to={newPath} className="erp-btn-primary">
                    Novo produto
                  </Link>
                </>
              ) : null}
            </div>
          )
        }
      />

      <div className="erp-table-wrap">
        {loading ? (
          <p className="erp-loading">Carregando...</p>
        ) : sortedItems.length === 0 ? (
          <p className="erp-empty">Nenhum produto cadastrado.</p>
        ) : (
          <div className="erp-table-scroll">
            <table className="erp-table">
              <thead>
                <tr>
                  {canWrite ? <th className="w-10" /> : null}
                  <th>Produto</th>
                  <th>Slug</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {sortedItems.map((product, index) => (
                  <tr key={product.id}>
                    {canWrite ? (
                      <td>
                        <SortOrderControls
                          canMoveUp={index > 0}
                          canMoveDown={index < sortedItems.length - 1}
                          disabled={moving}
                          onMoveUp={() => void move(index, 'up')}
                          onMoveDown={() => void move(index, 'down')}
                        />
                      </td>
                    ) : null}
                    <td>
                      <div className="flex items-center gap-3">
                        {product.logoBase64 ? (
                          <img src={product.logoBase64} alt="" className="h-10 w-10 rounded object-contain" />
                        ) : null}
                        <span className="font-medium text-secondary-900">{product.name}</span>
                      </div>
                    </td>
                    <td className="font-mono text-xs">{product.slug}</td>
                    <td>
                      <ActiveStatusBadge active={product.isActive} />
                    </td>
                    <td>
                      <div className="flex items-center gap-1">
                        {isProductArea ? (
                          <EditIconLink to={editPath(product.id)} label="Abrir produto" />
                        ) : (
                          <ViewIconLink
                            to={
                              isOffer
                                ? `/sales/products/${product.id}?from=offer`
                                : `/sales/products/${product.id}`
                            }
                            label="Ver oferta"
                          />
                        )}
                        {canWrite && !isProductArea ? (
                          <>
                            <EditIconLink to={editPath(product.id)} label="Editar produto" />
                            <DeleteIconButton label="Excluir produto" onClick={() => void handleDelete(product.id)} />
                          </>
                        ) : null}
                        {canWrite && isProductArea ? (
                          <DeleteIconButton label="Excluir produto" onClick={() => void handleDelete(product.id)} />
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <ListPaginationBar
        totalCount={totalCount}
        page={page}
        pageSize={pageSize}
        onPrevious={() => setPage((current) => Math.max(1, current - 1))}
        onNext={() => setPage((current) => Math.min(totalPages, current + 1))}
      />
    </PageShell>
  );
}
